import Tenant from "@/models/tenant/tenant";
import { InvitedUser, UserWithRole } from "@/models/users/User";
import { createContext, Dispatch, SetStateAction, useContext } from "react";
import { User } from "@auth0/auth0-spa-js";
import { Pagination } from "@/models/base/Pagination";
import type {
  InvitedUserSortBy,
  UserSortBy,
} from "@/services/userManagementService";
import type { GroupSortBy } from "@/pages/settings/user-management/utils/sortGroups";
import type { OrphanedWorkspaceSortBy } from "@/services/workspaceServices";

interface UserManagementContextValue {
  users: UserWithRole[];
  tenant: Tenant | null;
  invitedUsers: Pagination<InvitedUser>;
  authUser: User | undefined;
  openInviteUserDialog: boolean;
  setOpenInviteUserDialog: Dispatch<SetStateAction<boolean>>;
  openCreateGroupDialog: boolean;
  setOpenCreateGroupDialog: Dispatch<SetStateAction<boolean>>;
  bulkMode: boolean;
  setBulkMode: Dispatch<SetStateAction<boolean>>;
  selectedUsers: Set<string>;
  setSelectedUsers: Dispatch<SetStateAction<Set<string>>>;
  existingUsersPageNumber: number;
  setExistingUsersPageNumber: Dispatch<SetStateAction<number>>;
  existingUsersPageSize: number;
  setExistingUsersPageSize: Dispatch<SetStateAction<number>>;
  existingUsersPageCount: number;
  existingUsersSearch: string;
  setExistingUsersSearch: Dispatch<SetStateAction<string>>;
  existingUsersSortBy: UserSortBy;
  existingUsersSortAscending: boolean;
  /** Sorts by the given column; re-selecting the current column flips the direction. */
  setExistingUsersSort: (sortBy: UserSortBy) => void;
  invitedUsersSortBy: InvitedUserSortBy;
  invitedUsersSortAscending: boolean;
  setInvitedUsersSort: (sortBy: InvitedUserSortBy) => void;
  groupsSearch: string;
  setGroupsSearch: Dispatch<SetStateAction<string>>;
  groupsSortBy: GroupSortBy;
  groupsSortAscending: boolean;
  setGroupsSort: (sortBy: GroupSortBy) => void;
  orphanedWorkspacesSearch: string;
  setOrphanedWorkspacesSearch: Dispatch<SetStateAction<string>>;
  orphanedWorkspacesSortBy: OrphanedWorkspaceSortBy;
  orphanedWorkspacesSortAscending: boolean;
  setOrphanedWorkspacesSort: (sortBy: OrphanedWorkspaceSortBy) => void;
  invitedUsersPageNumber: number;
  setInvitedUsersPageNumber: Dispatch<SetStateAction<number>>;
  invitedUsersPageSize: number;
  setInvitedUsersPageSize: Dispatch<SetStateAction<number>>;
  isLoadingTenant: boolean;
  isLoadingUsers: boolean;
  isLoadingInvitedUsers: boolean;
  /**
   * The rows on screen belong to the previous sort or page while the new ones
   * load. Deliberately separate from `isLoading*`: folding it in there would
   * put the table back to flashing skeletons on every sort click (LAR-410).
   */
  isRefreshingUsers: boolean;
  isRefreshingInvitedUsers: boolean;
}

export const UserManagementContext = createContext<
  UserManagementContextValue | undefined
>(undefined);

export const useUserManagementContext = () => {
  const ctx = useContext(UserManagementContext);
  if (!ctx)
    throw new Error(
      "useUserManagementContext must be used within a UserManagementProvider"
    );
  return ctx;
};
